import {
  ChangeEvent, Dispatch, useRef, useState,
} from 'react';
import {
  Row, Col, TextInput,
} from 'nhsuk-react-components';
import { RuleEditor } from '../../../../../schemas/IterationRule.schema';
import { RuleActions } from '../../../../../reducers/rule/ruleReducer';
import {
  getWidth, isValidInput,
  getRuleEditorControl,
  getRuleComparatorVariables,
  updateRuleComparator,
  hasVaccineTargetControl,
  updateRuleName,
} from '../../../../../utils/ruleEditorUtils';
import { conditionTargetToLabel } from '../../../../../utils/formattingUtils';
import { ErrorsDisplay } from '../ErrorsDisplay';
import { VaccineTarget } from '../Controls/VaccineTarget';

export interface TextEditorProps {
  ruleEditor?: RuleEditor,
  dispatch: Dispatch<RuleActions>;
  value: string;
  conditionType: string;
  errors: Record<string, string>;
}

export function TextEditor({
  ruleEditor,
  dispatch,
  value,
  conditionType,
  errors,
}: TextEditorProps) {
  const initializedRef = useRef(false);

  const [showValidationError, setShowValidationError] = useState(false);

  const displayValue = getRuleComparatorVariables(value, ruleEditor?.ComparatorTemplate);

  const {
    label, errorMessage, regEx, placeholderText, helperText, width, defaultValue,
  } = getRuleEditorControl(ruleEditor, 'TEXT');

  const getCurrentValue = (
    displayVars: string[],
    initialized: boolean,
    defaultVal?: string,
  ) => {
    if (displayVars.length > 0) {
      const [firstDisplay] = displayVars;
      return firstDisplay;
    }

    if (!initialized && defaultVal) {
      return defaultVal;
    }

    return '';
  };

  const currentValue = getCurrentValue(displayValue, initializedRef.current, defaultValue);

  const hasVaccineTarget = hasVaccineTargetControl(ruleEditor);

  /// This approach (using a setTimeout) is less than ideal, but it ensures that the default value is set on initial render
  /// if a default value is provided and no value is currently set.

  /// A better approach might be to use a useEffect hook (couldnt get to work), but that would require more extensive changes
  /// Other options might be worth exploring - FlushSync was also tried but caused the UI to freeze

  /// SPIKE https://nhsd-jira.digital.nhs.uk/browse/SAI-3525 to explore better options for setting default values in rule editors

  if (initializedRef.current === false && displayValue.length === 0 && defaultValue) {
    setTimeout(() => {
      const formattedVars = conditionType && hasVaccineTarget
        ? [conditionTargetToLabel(conditionType), defaultValue]
        : [defaultValue];
      updateRuleComparator(dispatch, ruleEditor, defaultValue, [defaultValue], formattedVars);
    }, 0);

    initializedRef.current = true;
  }

  const handleConditionTargetChange = (e: ChangeEvent<HTMLSelectElement>) => {
    dispatch({ type: 'SET_CONDITION_TYPE', payload: e.target.value });
    updateRuleName(dispatch, ruleEditor, [conditionTargetToLabel(e.target.value), ...displayValue]);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setShowValidationError(false);
    const inputValue = e.target.value;

    let validateInput = true;
    if (inputValue !== '') {
      validateInput = isValidInput(inputValue, regEx);
      if (!validateInput) {
        setShowValidationError(true);
      }
    }

    if (validateInput) {
      const formattedVars = conditionType && hasVaccineTarget
        ? [conditionTargetToLabel(conditionType), inputValue]
        : [inputValue];
      updateRuleComparator(dispatch, ruleEditor, inputValue, [inputValue], formattedVars);
    }
  };

  const getErrorMessage = () => {
    if (showValidationError) {
      return errorMessage;
    }

    return undefined;
  };

  return (
    <>
      {hasVaccineTarget && (
        <Row>
          <Col width="full">
            <VaccineTarget
              value={conditionType}
              onChange={handleConditionTargetChange}
            />
          </Col>
        </Row>
      )}
      <Row>
        <Col width="full">
          <TextInput
            id="rule-comparator"
            data-testid="rule-comparator"
            label={label}
            name="Comparator"
            onChange={handleChange}
            value={currentValue}
            error={getErrorMessage()}
            placeholder={placeholderText}
            hint={!getErrorMessage() ? helperText : undefined}
            width={getWidth(width)}
          />
        </Col>
      </Row>
      <ErrorsDisplay errors={errors} errorKeys={['Comparator', 'AttributeName']} />
    </>
  );
}
